import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { CheckCircle, XCircle, Loader, Package, CreditCard } from 'lucide-react';
import SEO from '../components/SEO';
import Cookies from 'js-cookie';
import { trackEcommerce } from '../utils/analytics';

interface OrderDetails {
  id: string;
  status: string;
  customerEmail: string;
  shippingAddress?: any;
  paymentMethod?: string;
  items?: any[];
  totalPrice?: number;
  tax?: number;
}

const OrderConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { clearCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cartCleared, setCartCleared] = useState(false);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id') || searchParams.get('orderId');
  const paymentMethod = searchParams.get('paymentMethod');

  useEffect(() => {
    const verifyPayment = async () => {
      if (sessionId) {
        // Verify Stripe payment
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const token = Cookies.get('authToken');
          
          const response = await fetch(`${apiUrl}/payment/verify-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ sessionId })
          });

          if (!response.ok) {
            throw new Error('Failed to verify payment');
          }

          const data = await response.json();
          setOrderDetails({
            id: data.orderId,
            status: data.status,
            customerEmail: data.customerEmail,
            shippingAddress: data.shippingAddress,
            paymentMethod: 'credit',
            items: data.items,
            totalPrice: data.totalPrice,
            tax: data.tax
          });
          
          // Track purchase event for successful Stripe payment
          if (data.status === 'paid' && data.items && data.totalPrice) {
            trackEcommerce.purchase(data.orderId, data.items, data.totalPrice, data.tax || 0);
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          setError('Failed to verify payment. Please contact support.');
        }
      } else if (orderId && paymentMethod === 'pickup') {
        // Handle pay at pickup confirmation
        setOrderDetails({
          id: orderId,
          status: 'pending_payment',
          customerEmail: user?.email || '',
          paymentMethod: 'pickup'
        });
      } else {
        setError('No order information found');
      }
      
      setLoading(false);
    };

    verifyPayment();
  }, [sessionId, orderId, paymentMethod, user]);

  // Clear cart when order is confirmed
  useEffect(() => {
    if (orderDetails && !cartCleared) {
      const isSuccessful = orderDetails.status === 'paid' || 
                          orderDetails.status === 'completed' || 
                          orderDetails.paymentMethod === 'pickup';
      
      if (isSuccessful) {
        clearCart();
        setCartCleared(true);
      }
    }
  }, [orderDetails, cartCleared, clearCart]);

  if (loading) {
    return (
      <>
        <SEO 
          title="Processing Order"
          description="Processing your order..."
        />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <Loader className="h-16 w-16 animate-spin mx-auto mb-4 text-keystone-gold" />
            <h1 className="text-2xl font-bold mb-4">Processing Your Order...</h1>
            <p className="text-gray-600">Please wait while we confirm your order details.</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO 
          title="Order Error"
          description="There was an error processing your order"
        />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-4">Order Error</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-keystone-blue text-white px-6 py-3 rounded-lg hover:bg-keystone-blue-dark transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </>
    );
  }

  const isPaymentSuccessful = orderDetails?.status === 'paid' || orderDetails?.status === 'completed';
  const isPayAtPickup = orderDetails?.paymentMethod === 'pickup';

  return (
    <>
      <SEO 
        title={isPaymentSuccessful || isPayAtPickup ? "Order Confirmed" : "Order Processing"}
        description="Your order has been received and is being processed"
      />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              {isPaymentSuccessful || isPayAtPickup ? (
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              ) : (
                <Package className="h-16 w-16 text-keystone-gold mx-auto mb-4" />
              )}
              
              <h1 className="text-3xl font-bold mb-2">
                {isPaymentSuccessful ? 'Payment Successful!' : 
                 isPayAtPickup ? 'Order Confirmed!' : 
                 'Order Received'}
              </h1>
              
              <p className="text-gray-600 text-lg">
                {isPaymentSuccessful ? 'Thank you for your payment. Your order is being processed.' :
                 isPayAtPickup ? 'Your order has been received. Please pay when you pick up.' :
                 'We are processing your order.'}
              </p>
            </div>

            <div className="border-t border-b py-6 mb-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Order ID:</span>
                  <span className="text-gray-700">{orderDetails?.id}</span>
                </div>
                
                {orderDetails?.customerEmail && (
                  <div className="flex justify-between">
                    <span className="font-medium">Email:</span>
                    <span className="text-gray-700">{orderDetails.customerEmail}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="font-medium">Payment Method:</span>
                  <span className="text-gray-700 flex items-center">
                    {isPayAtPickup ? (
                      <>
                        <Package className="h-4 w-4 mr-2" />
                        Pay at Pickup
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Credit Card
                      </>
                    )}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span>
                  <span className={`font-medium ${
                    isPaymentSuccessful ? 'text-green-600' : 
                    isPayAtPickup ? 'text-yellow-600' : 
                    'text-gray-600'
                  }`}>
                    {isPaymentSuccessful ? 'Paid' : 
                     isPayAtPickup ? 'Awaiting Pickup Payment' : 
                     'Processing'}
                  </span>
                </div>
              </div>
            </div>

            {orderDetails?.shippingAddress && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Shipping Address</h3>
                <div className="text-gray-700">
                  <p>{orderDetails.shippingAddress.name}</p>
                  <p>{orderDetails.shippingAddress.address.line1}</p>
                  {orderDetails.shippingAddress.address.line2 && (
                    <p>{orderDetails.shippingAddress.address.line2}</p>
                  )}
                  <p>
                    {orderDetails.shippingAddress.address.city}, {orderDetails.shippingAddress.address.state} {orderDetails.shippingAddress.address.postal_code}
                  </p>
                  <p>{orderDetails.shippingAddress.address.country}</p>
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold mb-3">What's Next?</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-keystone-gold mr-2">✓</span>
                  <span>You will receive an email confirmation shortly</span>
                </li>
                <li className="flex items-start">
                  <span className="text-keystone-gold mr-2">✓</span>
                  <span>Our team will review your order and begin production</span>
                </li>
                <li className="flex items-start">
                  <span className="text-keystone-gold mr-2">✓</span>
                  <span>We'll notify you when your order is ready for pickup</span>
                </li>
                {isPayAtPickup && (
                  <li className="flex items-start">
                    <span className="text-keystone-gold mr-2">✓</span>
                    <span>Payment will be collected when you pick up your order</span>
                  </li>
                )}
              </ul>
            </div>

            <div className="text-center space-y-4">
              <button
                onClick={() => navigate('/profile')}
                className="bg-keystone-gold text-keystone-blue px-6 py-3 rounded-lg font-semibold hover:bg-keystone-gold-600 transition-colors mr-4"
              >
                View My Orders
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderConfirmation;